import { useMemo, useState } from 'react'
import { Link as InertiaLink } from '@inertiajs/react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import dayjs from 'dayjs'

import { Table } from '@components/molecules/Table'
import { SortableHeader, TANSTACK_DEFAULT_COLUMN_SIZE } from '@components/molecules/DataTable'
import { callingSessionSupporterUrl } from '../../supporters-management/utils/routes'
import Link from '@atoms/Link'
import { scopedTranslation } from '@frontend/utils/I18n'
import { DropdownMenuItem } from '@components/atoms/DropdownMenu'
import { DotsMenu } from '@components/molecules/DotsMenu'
import { SupporterStatusBadge, type StatusType, type ActiveSkip } from './SupporterStatusBadge'
import { OutcomeBadge, type OutcomeType } from './OutcomeBadge'
import { CallbackNote } from './CallbackNote'
import Icon from '@components/utils/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import SearchField from '@components/molecules/SearchField'
import { usePrioritiseSupporter } from '../hooks/usePrioritiseSupporter'
import { useSkipSupporter } from '../hooks/useSkipSupporter'
import { useDialog } from '@components/DialogProvider'
import { whenRange } from '@utils/callbackTime'

const t = scopedTranslation('views.calling_sessions.view_all_supporters')
const tShared = scopedTranslation('shared')
const tSupporterDetails = scopedTranslation('components.supporter_details')

export type ScheduledCallback = {
  dueAt: string
  flexible: boolean
  assignee: string | null
  requestedBy: string | null
  notes: string
}

const CallbackDetails = ({ callback, held }: { callback: ScheduledCallback; held: boolean }) => {
  const assignee = callback.assignee ?? t('callback.any_leader')
  if (!held) {
    return (
      <div className="tw:flex tw:w-80 tw:max-w-full tw:flex-col tw:gap-1 tw:text-sm">
        <span className="tw:flex tw:items-start tw:gap-1.5 tw:text-gray-700">
          <Icon type="calendar" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" aria-hidden />
          {t('callback.due', { when: whenRange(callback.dueAt, new Date(), callback.flexible) })} · {assignee}
        </span>
        {callback.notes && <CallbackNote note={callback.notes} />}
      </div>
    )
  }
  const requestedBy = callback.requestedBy ?? t('callback.the_supporter')

  return (
    <div className="tw:flex tw:w-80 tw:max-w-full tw:items-start tw:gap-1.5 tw:text-sm tw:text-gray-500 tw:[&:has([aria-expanded=true])>span[aria-hidden]]:hidden tw:has-aria-expanded:flex-col tw:has-aria-expanded:gap-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:text-gray-700">
              <Icon
                type={callback.assignee ? 'user-01' : 'users-01'}
                className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500"
                aria-hidden
              />
              {assignee}
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('callback.requested_by', { name: requestedBy })}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {callback.notes && (
        <>
          <span aria-hidden>·</span>
          <CallbackNote note={callback.notes} />
        </>
      )}
    </div>
  )
}

export type CallingSessionSupporterRow = {
  id: string
  supporterId: string
  position: number
  status: string
  outcome?: string | null
  unavailableReason: string | null
  unavailableUntil: string | null
  lastCallAttemptAt?: string | null
  actionGroupId: string
  isUpNext?: boolean
  activeSkip?: ActiveSkip
  callback?: ScheduledCallback | null
  supporter: {
    id: string
    firstName: string | null
    lastName: string | null
  }
}

type CallingSessionSupportersDataTableProps = {
  sessionSupporters: CallingSessionSupporterRow[]
  callingSessionId: string
  nextSupporterToCallId?: string
  showOutcome?: boolean
  onAddToQueue?: (row: CallingSessionSupporterRow) => void
}

export const CallingSessionSupportersDataTable = ({
  sessionSupporters,
  callingSessionId,
  nextSupporterToCallId,
  showOutcome = false,
  onAddToQueue,
}: CallingSessionSupportersDataTableProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const { prioritise, isLoading } = usePrioritiseSupporter({
    alertsPath: 'viewAllSupporters',
    reloadProps: ['callingSession'],
  })
  const { unskip, isLoading: isSkipLoading } = useSkipSupporter({
    alertsPath: 'viewAllSupporters',
    reloadProps: ['callingSession'],
  })
  const { open: openDialog } = useDialog()

  const filteredSupporters = useMemo(() => {
    if (!searchQuery.trim()) {
      return sessionSupporters
    }
    const query = searchQuery.trim().toLowerCase()
    return sessionSupporters.filter(({ supporter }) => {
      const fullName = `${supporter.firstName} ${supporter.lastName}`.toLowerCase()
      return fullName.includes(query)
    })
  }, [sessionSupporters, searchQuery])

  const columns: Array<ColumnDef<CallingSessionSupporterRow>> = useMemo(
    () => [
      {
        accessorKey: 'position',
        size: 160,
        enableResizing: false,
        header: ({ column }) => (
          <SortableHeader column={column} className="tw:mx-auto tw:whitespace-nowrap">
            {t('columns.position')}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="tw:text-center">
            <strong className="tw:font-bold">{row.getValue('position')}</strong>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        accessorFn: ({ supporter }) => `${supporter.firstName} ${supporter.lastName}`,
        header: ({ column }) => (
          <SortableHeader column={column} className="tw:whitespace-nowrap">
            {t('columns.name')}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const { supporter, isUpNext } = row.original
          const sessionSupporterUrl = callingSessionSupporterUrl(callingSessionId, supporter.id)
          return (
            <div className="tw:flex tw:items-center tw:gap-2 tw:group-data-[up-next=true]:bg-success-100 tw:group-data-[up-next=true]:text-success-700">
              {isUpNext ? (
                <p className="tw:flex tw:items-baseline tw:gap-2">
                  <span className="tw:w-2 tw:h-2 tw:self-center tw:rounded-full tw:bg-success-500" />
                  <Link href={sessionSupporterUrl} size="sm" rank="link" color="primary" as="inertiaLink">
                    {row.getValue('name')}
                  </Link>
                  <span className="tw:text-xs">({t('current_supporter_label')})</span>
                </p>
              ) : (
                <Link href={sessionSupporterUrl} size="sm" rank="link" color="primary" as="inertiaLink">
                  {row.getValue('name')}
                </Link>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <SortableHeader column={column} className="tw:whitespace-nowrap">
            {t('columns.status')}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const status = row.getValue<StatusType>('status')
          if (!status) return <div />
          const { callback } = row.original
          return (
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:items-start tw:gap-1.5">
              <SupporterStatusBadge
                status={status as StatusType}
                unavailableReason={row.original.unavailableReason}
                unavailableUntil={row.original.unavailableUntil}
                activeSkip={row.original.activeSkip}
                callbackDueAt={callback?.dueAt}
                callbackFlexible={callback?.flexible}
              />
              {callback && <CallbackDetails callback={callback} held={status === 'skipped_callback'} />}
            </div>
          )
        },
      },
      ...(showOutcome
        ? [
            {
              accessorKey: 'outcome',
              header: ({ column }) => (
                <SortableHeader column={column} className="tw:whitespace-nowrap">
                  {t('columns.outcome')}
                </SortableHeader>
              ),
              cell: ({ row }) => {
                const outcome = row.getValue<OutcomeType | null>('outcome')
                if (!outcome) return null
                return <OutcomeBadge outcome={outcome} />
              },
            } satisfies ColumnDef<CallingSessionSupporterRow>,
          ]
        : []),
      {
        accessorKey: 'lastCallAttemptAt',
        sortingFn: (rowA, rowB) => {
          const lastCallAttemptAtA = rowA.original.lastCallAttemptAt
          const lastCallAttemptAtB = rowB.original.lastCallAttemptAt
          if (!lastCallAttemptAtA && !lastCallAttemptAtB) return 0
          if (!lastCallAttemptAtA) return 1
          if (!lastCallAttemptAtB) return -1
          return lastCallAttemptAtA.localeCompare(lastCallAttemptAtB)
        },
        header: ({ column }) => (
          <SortableHeader column={column} className="tw:whitespace-nowrap">
            {t('columns.last_call_attempt_at')}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const lastCallAttemptAt = row.getValue<string | null>('lastCallAttemptAt')
          if (!lastCallAttemptAt) return null
          try {
            const date = dayjs(lastCallAttemptAt)
            if (!date.isValid()) return null
            return <div>{date.format('D MMM YYYY, h:mm A')}</div>
          } catch {
            return null
          }
        },
      },
      {
        id: 'actions',
        header: () => <div className="tw:whitespace-nowrap tw:text-right tw:font-bold">{t('columns.actions')}</div>,
        cell: ({ row }) => {
          const { id, supporterId, status, isUpNext, supporter } = row.original
          const supporterName = `${supporter.firstName ?? ''} ${supporter.lastName ?? ''}`.trim()
          const canPrioritise = status !== 'completed' && !isUpNext
          const canSkip = status !== 'completed'
          const hasActiveLeaderSkip = status === 'skipped_by_leader'
          const heldForCallback = status === 'skipped_callback'

          return (
            <div className="tw:flex tw:justify-end">
              <DotsMenu ariaLabel={t('actions_menu.open_menu')}>
                <DropdownMenuItem role="link" asChild>
                  <InertiaLink href={callingSessionSupporterUrl(callingSessionId, supporterId)}>
                    {t('actions_menu.view_profile')}
                  </InertiaLink>
                </DropdownMenuItem>
                {canPrioritise && (
                  <DropdownMenuItem
                    onClick={() => prioritise({ callingSessionSupporterId: id, supporterName })}
                    disabled={isLoading}
                  >
                    {t('actions_menu.call_next')}
                  </DropdownMenuItem>
                )}
                {heldForCallback && onAddToQueue ? (
                  <DropdownMenuItem onClick={() => onAddToQueue(row.original)}>
                    {t('actions_menu.add_to_queue')}
                  </DropdownMenuItem>
                ) : hasActiveLeaderSkip ? (
                  <DropdownMenuItem
                    onClick={() => unskip({ callingSessionSupporterId: id, supporterName })}
                    disabled={isSkipLoading}
                    className="tw:text-warning-700 tw:data-highlighted:text-warning-700"
                  >
                    {tSupporterDetails('actions.remove_skip')}
                  </DropdownMenuItem>
                ) : (
                  canSkip && (
                    <DropdownMenuItem
                      onClick={() =>
                        openDialog({
                          type: 'skipSupporter',
                          dialogData: { callingSessionSupporterId: id, supporterName },
                        })
                      }
                      disabled={isSkipLoading}
                    >
                      {tSupporterDetails('actions.skip_for')}
                    </DropdownMenuItem>
                  )
                )}
              </DotsMenu>
            </div>
          )
        },
        enableSorting: false,
      },
    ],
    [callingSessionId, nextSupporterToCallId, showOutcome, onAddToQueue]
  )

  const table = useReactTable({
    data: filteredSupporters,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  })

  return (
    <div className="tw:space-y-4">
      <div className="tw:flex tw:items-center tw:w-full tw:md:w-[300px]">
        <SearchField
          fieldName="search"
          displayName={t('search_placeholder')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(event.target.value)
          }}
        />
      </div>

      {filteredSupporters.length === 0 && searchQuery.trim() ? (
        <div className="tw:text-center tw:py-8 tw:text-gray-500">{t('empty_search_state')}</div>
      ) : filteredSupporters.length === 0 ? (
        <div className="tw:text-center tw:py-8 tw:text-gray-500">{t('empty_state')}</div>
      ) : (
        <div className="tw:rounded-md tw:border">
          <Table.Root>
            <Table.Header>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Head
                      key={header.id}
                      style={{
                        width: header.getSize() !== TANSTACK_DEFAULT_COLUMN_SIZE ? `${header.getSize()}px` : undefined,
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.Head>
                  ))}
                </Table.Row>
              ))}
            </Table.Header>
            <Table.Body>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <Table.Row key={row.id} className="group" data-up-next={row.original.isUpNext ? 'true' : undefined}>
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{
                          width:
                            cell.column.getSize() !== TANSTACK_DEFAULT_COLUMN_SIZE
                              ? `${cell.column.getSize()}px`
                              : undefined,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={columns.length} className="tw:h-24 tw:text-center">
                    {tShared('search.no_results')}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </div>
      )}
    </div>
  )
}
